import * as React from "react";
import { useState, useCallback } from "react";
import type { CSSProperties } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { Text, ITextStyles } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { TooltipHost } from "@fluentui/react";
import { MessageBar, MessageBarType } from "@fluentui/react";
import { Stack } from "@fluentui/react";
import { TeachingBubble } from "@fluentui/react";
import { Email } from "@src/types/api";
import type { ShipmentRowVM } from "@taskpane/presentation/shipments/ShipmentRowBuilder";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import { createShipmentActions } from "@src/load-agent/app/TaskpaneServicesContext";
import { useOfficeItemMeta } from "@taskpane/hooks/useOfficeItemMeta";
import { useShipments } from "@taskpane/hooks/useShipments";
import { useOpenReplyDraft } from "@taskpane/hooks/useOpenReplyDraft";
import { useStatuses } from "@taskpane/hooks/useStatuses";
import { resolveShipmentStatus } from "@shared/status/statusResolver";
import { StatusPill } from "./StatusPill";
import type { ShipmentRowActionsApi } from "@src/load-agent/integrations/shipmentRowActions";
import { emailShipmentsFtl, emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import { resolveFtlOfferBaseUsd } from "@shared/utils/resolveFtlOfferBaseUsd";
import ShipmentRow from "./ShipmentRow";
import OfferSelectionModal from "./OfferSelectionModal";
import { shipficiencyLogo } from "@src/branding/shipficiencyLogo";
import { strings } from "@src/i18n/strings";
import { colors } from "@taskpane/theme";

const STORAGE_KEY_ATTACHMENT_FALLBACK = "autobidder_lastBookingAttachmentUrls";
const FALLBACK_TTL_MS = 10 * 60 * 1000;
const PROCESSING_POLL_MS = 5000;
const shipmentsStrings = strings.components.shipments;

function loadAttachmentFallbackFromStorage():
  | {
      name: string;
      url: string;
    }[]
  | null {
  try {
    const raw =
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(STORAGE_KEY_ATTACHMENT_FALLBACK)
        : null;

    if (!raw) return null;
    const {
      urls,
      at,
    }: {
      urls: {
        name: string;
        url: string;
      }[];
      at: number;
    } = JSON.parse(raw);

    if (!Array.isArray(urls) || urls.length === 0 || Date.now() - at > FALLBACK_TTL_MS) return null;
    return urls;
  } catch {
    return null;
  }
}

function saveAttachmentFallbackToStorage(
  urls:
    | {
        name: string;
        url: string;
      }[]
    | null
): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    if (!urls?.length) {
      sessionStorage.removeItem(STORAGE_KEY_ATTACHMENT_FALLBACK);
      return;
    }
    sessionStorage.setItem(
      STORAGE_KEY_ATTACHMENT_FALLBACK,
      JSON.stringify({ urls, at: Date.now() })
    );
  } catch {
    // ignore session storage failures
  }
}

function getSelectedOffersForShipment(
  emailData: Email | null,
  mode: "LTL" | "FTL",
  shipmentId: string
): Array<{
  id: string;
  name: string;
  price: number;
}> {
  if (!emailData) return [];
  if (mode === "LTL") {
    const shipment = emailShipmentsLtl(emailData).find((s) => s.shipmentId === shipmentId);

    if (!shipment) return [];
    const sentIds = new Set(shipment.sentOffers ?? []);

    return shipment.quotedRates
      .filter((o) => sentIds.has(o.id))
      .map((o) => ({ id: o.id, name: o.carrierName, price: o.price }));
  } else {
    const shipment = emailShipmentsFtl(emailData).find((s) => s.shipmentId === shipmentId);

    if (!shipment) return [];
    const sentIds = new Set(shipment.sentOffers ?? []);

    return shipment.quotedRates
      .filter((o) => sentIds.has(o.id))
      .map((o) => ({ id: o.id, name: o.name ?? "", price: resolveFtlOfferBaseUsd(o) }));
  }
}

function isIgnoredEmail(emailData: Email | null): boolean {
  return (emailData?.emailType?.toLowerCase() ?? "") === "ignore";
}

const uiStyles = {
  container: {},
  headerText: { root: { fontSize: "x-small" } } as ITextStyles,
  errorWrapper: { marginBottom: "0.5rem" },
  ignoredEmptyState: {
    padding: "1.5rem 1rem",
    textAlign: "center" as const,
    color: "#5a6a7a",
    fontSize: "0.9rem",
    lineHeight: 1.5,
  },
  desktopFallbackWrapper: {
    marginBottom: "0.5rem",
    padding: "12px",
    border: `1px solid ${colors.primary}`,
    borderRadius: "4px",
    backgroundColor: "#f3f9fd",
  },
  buttonContainer: {},
  historicalIconRoot: { fontWeight: "bolder" },
  innerContainer: {},
  headerContainer: {},
  headerRow: {},
  customerVerifyIconOk: {
    root: { fontSize: 14, lineHeight: "14px", flexShrink: 0, color: "#107c10" },
  },
  customerVerifyIconWarn: {
    root: { fontSize: 14, lineHeight: "14px", flexShrink: 0, color: "#a4262c" },
  },
  headerRowWithReply: {
    root: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "8px",
    },
  },
  createReplyButton: {
    root: { height: "24px", minWidth: 0, padding: "0 8px", flexShrink: 0 },
    label: { fontSize: "12px", margin: 0 },
  },
  bodyContainer: {},
  statusPage: {
    position: "relative",
    minHeight: 240,
  },
  processingOverlayMessage: {
    root: {
      position: "absolute",
      top: "46px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 2,
      padding: "6px 12px",
      borderRadius: "999px",
      backgroundColor: "rgba(255, 255, 255, 0.92)",
      border: "1px solid #edebe9",
      color: "#323130",
      fontSize: "0.8rem",
      fontWeight: 600,
      letterSpacing: "0.2px",
      textAlign: "center",
      whiteSpace: "nowrap",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
  } as ITextStyles,
  skeletonPage: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    padding: "18px 12px 12px",
  },
  skeletonHeader: {
    display: "grid",
    gridTemplateColumns: "56px 1fr",
    gap: "12px",
    alignItems: "center",
  },
  skeletonLines: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
  },
  skeletonActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "4px",
  },
  skeletonBlock: {
    height: 220,
    borderRadius: "4px",
  },
  failedPage: {
    position: "relative",
    minHeight: 240,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "24px 12px",
    textAlign: "center",
  },
  failedImage: {
    width: 160,
    maxWidth: "70%",
    height: "auto",
    objectFit: "contain",
  } as CSSProperties,
  failureContext: {
    maxWidth: 320,
    color: "#605e5c",
    fontSize: "0.85rem",
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
  },
};

function ProcessingShipmentSkeleton() {
  return (
    <Box sx={uiStyles.skeletonPage} aria-label={shipmentsStrings.aria.processingSkeleton}>
      <Box sx={uiStyles.skeletonHeader}>
        <Skeleton variant="circular" width={56} height={56} />
        <Box sx={uiStyles.skeletonLines}>
          <Skeleton variant="rounded" width="64%" height={14} />
          <Skeleton variant="rounded" width="48%" height={14} />
          <Skeleton variant="rounded" width="72%" height={14} />
        </Box>
      </Box>
      <Box sx={uiStyles.skeletonLines}>
        <Skeleton variant="rounded" width="100%" height={14} />
        <Skeleton variant="rounded" width="86%" height={14} />
        <Skeleton variant="rounded" width="94%" height={14} />
        <Skeleton variant="rounded" width="72%" height={14} />
      </Box>
      <Box sx={uiStyles.skeletonActions}>
        <Skeleton variant="rounded" width={118} height={22} />
        <Skeleton variant="rounded" width={96} height={22} />
      </Box>
      <Skeleton variant="rounded" width="100%" sx={uiStyles.skeletonBlock} />
      <Box sx={uiStyles.skeletonActions}>
        <Skeleton variant="rounded" width={118} height={22} />
        <Skeleton variant="rounded" width={96} height={22} />
      </Box>
      <Box sx={uiStyles.skeletonHeader}>
        <Skeleton variant="circular" width={56} height={56} />
        <Box sx={uiStyles.skeletonLines}>
          <Skeleton variant="rounded" width="64%" height={14} />
          <Skeleton variant="rounded" width="48%" height={14} />
          <Skeleton variant="rounded" width="72%" height={14} />
        </Box>
      </Box>
    </Box>
  );
}

interface ShipmentsProps {
  emailData: Email | null;
  verifiedSender?: boolean;
  refreshEmailData?: () => Promise<unknown>;
  setActiveComponent: Function;
  goLTL: Function;
  goFTL: Function;
  shipmentActions?: ShipmentRowActionsApi;
}

export default function Shipments({
  emailData,
  verifiedSender,
  refreshEmailData,
  goLTL,
  goFTL,
  shipmentActions: shipmentActionsProp,
}: ShipmentsProps) {
  const { statuses } = useStatuses();
  const { passedTime } = useOfficeItemMeta();
  const [desktopAttachmentFallback, setDesktopAttachmentFallback] = useState<
    | {
        name: string;
        url: string;
      }[]
    | null
  >(loadAttachmentFallbackFromStorage);
  const [historicalBubbleVisible, setHistoricalBubbleVisible] = useState(false);
  const [offerModal, setOfferModal] = useState<{
    open: boolean;
    row: {
      mode: "LTL" | "FTL";
      shipmentId: string;
    };
    offers: Array<{
      id: string;
      name: string;
      price: number;
    }>;
  }>({ open: false, row: { mode: "LTL", shipmentId: "" }, offers: [] });
  const ports = useTaskpaneServices();
  const shipmentActions: ShipmentRowActionsApi = React.useMemo(() => {
    if (shipmentActionsProp) {
      return shipmentActionsProp;
    }
    if (emailData) {
      return createShipmentActions(
        emailData,
        { loadApp: ports.loadApp },
        undefined,
        undefined,
        refreshEmailData
      );
    }
    return {} as ShipmentRowActionsApi;
  }, [shipmentActionsProp, emailData, ports.loadApp, refreshEmailData]);
  const {
    rows,
    selectionState,
    error,
    setError,
    toggleSelection,
    canCreateReply,
    loadingShipments,
    setLoadingShipment,
  } = useShipments(emailData, verifiedSender);
  const hasProcessingShipment = rows.some(
    (row) => row.loadStatus === strings.shared.shipmentStatus.processing
  );
  const hasFailedShipment = rows.some(
    (row) => row.loadStatus === strings.shared.shipmentStatus.failed
  );
  const failureContext =
    rows
      .find((row) => row.loadStatus === strings.shared.shipmentStatus.failed)
      ?.failureContext?.trim() ?? "";

  React.useEffect(() => {
    if (!hasProcessingShipment || !refreshEmailData) return;
    let inFlight = false;
    const interval = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void refreshEmailData().finally(() => {
        inFlight = false;
      });
    }, PROCESSING_POLL_MS);

    return () => window.clearInterval(interval);
  }, [hasProcessingShipment, refreshEmailData]);
  const handleCreateNewQuote = React.useCallback(() => {
    if (emailShipmentsLtl(emailData).length > 0) {
      goLTL(null);
    } else {
      goFTL(null);
    }
  }, [emailData, goLTL, goFTL]);
  const openReplyDraft = useOpenReplyDraft();
  const createReply = React.useCallback(() => {
    openReplyDraft({
      selectedIds: selectionState.selectedIds,
      emailData,
      internetMessageId: emailData?.internetMessageId,
      refreshEmailData,
      onError: setError,
    });
  }, [openReplyDraft, selectionState.selectedIds, emailData, refreshEmailData]);
  const handleShipmentClick = React.useCallback(
    (row: ShipmentRowVM) => {
      if (row.mode === "LTL") {
        const ltlShipment = emailData
          ? emailShipmentsLtl(emailData).find((s) => s.shipmentId === row.shipmentId)
          : undefined;

        if (ltlShipment) goLTL(ltlShipment);
      } else if (row.mode === "FTL") {
        const ftlShipment = emailData
          ? emailShipmentsFtl(emailData).find((s) => s.shipmentId === row.shipmentId)
          : undefined;

        if (ftlShipment) goFTL(ftlShipment);
      }
    },
    [emailData, goLTL, goFTL]
  );
  const handleBookClick = useCallback(
    async (row: { mode: "LTL" | "FTL"; shipmentId: string }) => {
      const selectedOffers = getSelectedOffersForShipment(emailData, row.mode, row.shipmentId);

      if (selectedOffers.length > 1) {
        setOfferModal({ open: true, row, offers: selectedOffers });
        return;
      }
      setLoadingShipment(row.shipmentId, true);
      setDesktopAttachmentFallback(null);
      try {
        const result = await shipmentActions.book({ mode: row.mode, shipmentId: row.shipmentId });

        if (result.attachmentDownloadUrls?.length) {
          setDesktopAttachmentFallback(result.attachmentDownloadUrls);
          saveAttachmentFallbackToStorage(result.attachmentDownloadUrls);
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : shipmentsStrings.defaults.failedBookingAdapter;

        setError(message);
      } finally {
        setLoadingShipment(row.shipmentId, false);
      }
    },
    [emailData, shipmentActions, setLoadingShipment]
  );
  const handleOfferModalSubmit = useCallback(
    async (selectedOfferId: string) => {
      const { row } = offerModal;

      setOfferModal((prev) => ({ ...prev, open: false }));
      setLoadingShipment(row.shipmentId, true);
      setDesktopAttachmentFallback(null);
      try {
        const result = await shipmentActions.book({
          mode: row.mode,
          shipmentId: row.shipmentId,
          selectedOfferId,
        });

        if (result.attachmentDownloadUrls?.length) {
          setDesktopAttachmentFallback(result.attachmentDownloadUrls);
          saveAttachmentFallbackToStorage(result.attachmentDownloadUrls);
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : shipmentsStrings.defaults.failedBookingAdapter;

        setError(message);
      } finally {
        setLoadingShipment(row.shipmentId, false);
      }
    },
    [offerModal, shipmentActions, setLoadingShipment]
  );
  const handleOfferModalCancel = useCallback(() => {
    setOfferModal((prev) => ({ ...prev, open: false }));
  }, []);
  const subject = emailData?.subject ?? shipmentsStrings.defaults.noSubject;
  const ignored = isIgnoredEmail(emailData);
  const showCreateReply = !ignored && !hasProcessingShipment && !hasFailedShipment;
  const sender = emailData?.sender?.trim() || "";
  const senderCustomerVerified = emailData?.organization?.senderCustomerVerified;
  const senderExactCustomerMatch = emailData?.organization?.senderExactCustomerMatch;
  const headerIdentityLabel = (() => {
    if (senderExactCustomerMatch === true && sender) return sender;
    if (senderCustomerVerified === true) {
      return emailData?.companyName ?? shipmentsStrings.defaults.companyName;
    }
    if (senderCustomerVerified === false && sender) return sender;
    return emailData?.companyName ?? sender ?? shipmentsStrings.defaults.companyName;
  })();

  return (
    <Box className="shipments-container" sx={uiStyles.container}>
      {error && (
        <Box sx={uiStyles.errorWrapper}>
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            onDismiss={() => setError(null)}
          >
            {error}
          </MessageBar>
        </Box>
      )}
      {desktopAttachmentFallback && desktopAttachmentFallback.length > 0 && (
        <Box sx={uiStyles.desktopFallbackWrapper}>
          <Stack tokens={{ childrenGap: 8 }}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
              {shipmentsStrings.fallback.intro}
            </Text>
            <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
              {desktopAttachmentFallback.map((a) => (
                <PrimaryButton
                  key={a.name}
                  text={shipmentsStrings.fallback.download(a.name)}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  primary
                />
              ))}
              <DefaultButton
                text={shipmentsStrings.fallback.dismiss}
                onClick={() => {
                  setDesktopAttachmentFallback(null);
                  saveAttachmentFallbackToStorage(null);
                }}
              />
            </Stack>
          </Stack>
        </Box>
      )}

      <Box className="shipments-button-container" sx={uiStyles.buttonContainer}>
        <DefaultButton
          text={shipmentsStrings.actions.createNewQuote}
          onClick={handleCreateNewQuote}
          allowDisabledFocus
          className="shipments-button"
        />

        <DefaultButton
          onClick={() => setHistoricalBubbleVisible((v) => !v)}
          allowDisabledFocus
          className="historical-button"
          id="historical-button"
          iconProps={{ iconName: "MoreVertical", styles: { root: uiStyles.historicalIconRoot } }}
        />

        {historicalBubbleVisible && (
          <TeachingBubble
            target="#historical-button"
            onDismiss={() => setHistoricalBubbleVisible(false)}
          >
            {shipmentsStrings.historical.comingSoon}
          </TeachingBubble>
        )}
      </Box>

      <Box className="shipments-inner-container" sx={uiStyles.innerContainer}>
        <Box className="shipments-header-container" sx={uiStyles.headerContainer}>
          <Stack
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 6 }}
            styles={uiStyles.headerRow}
          >
            <Text styles={uiStyles.headerText}>{headerIdentityLabel}</Text>
            {senderExactCustomerMatch === true && (
              <Icon
                iconName="SkypeCircleCheck"
                styles={uiStyles.customerVerifyIconOk}
                title={shipmentsStrings.verification.exactTitle}
                ariaLabel={shipmentsStrings.verification.exactAria}
              />
            )}
            {senderExactCustomerMatch !== true && senderCustomerVerified === true && (
              <Icon
                iconName="SkypeCircleCheck"
                styles={uiStyles.customerVerifyIconOk}
                title={shipmentsStrings.verification.domainTitle}
                ariaLabel={shipmentsStrings.verification.domainAria}
              />
            )}
            {senderCustomerVerified === false && (
              <Icon
                iconName="StatusErrorFull"
                styles={uiStyles.customerVerifyIconWarn}
                title={shipmentsStrings.verification.notFoundTitle}
                ariaLabel={shipmentsStrings.verification.notFoundAria}
              />
            )}
            <Text styles={uiStyles.headerText}>{passedTime}</Text>
          </Stack>

          <Stack horizontal styles={uiStyles.headerRowWithReply}>
            <Text styles={uiStyles.headerText}>{subject}</Text>
            {showCreateReply && (
              <TooltipHost
                content={canCreateReply ? undefined : shipmentsStrings.actions.createReplyDisabledHint}
              >
                <DefaultButton
                  text={shipmentsStrings.actions.createReply}
                  onClick={createReply}
                  disabled={!canCreateReply}
                  allowDisabledFocus
                  styles={uiStyles.createReplyButton}
                />
              </TooltipHost>
            )}
          </Stack>
        </Box>

        {ignored ? (
          <Box className="shipments-body-container" sx={uiStyles.bodyContainer}>
            <Box sx={uiStyles.ignoredEmptyState}>{shipmentsStrings.ignoredEmail}</Box>
          </Box>
        ) : hasProcessingShipment ? (
          <Box className="shipments-body-container" sx={uiStyles.bodyContainer}>
            <Box sx={uiStyles.statusPage}>
              <Text styles={uiStyles.processingOverlayMessage}>
                {shipmentsStrings.processingOverlay}
              </Text>
              <StatusPill
                status={resolveShipmentStatus(
                  { loadStatus: strings.shared.shipmentStatus.processing },
                  statuses
                )}
                topRight
              />
              <ProcessingShipmentSkeleton />
            </Box>
          </Box>
        ) : hasFailedShipment ? (
          <Box className="shipments-body-container" sx={uiStyles.bodyContainer}>
            <Box sx={uiStyles.failedPage}>
              <StatusPill
                status={resolveShipmentStatus(
                  { loadStatus: strings.shared.shipmentStatus.failed },
                  statuses
                )}
                topRight
              />
              <img
                src={shipficiencyLogo}
                alt={shipmentsStrings.failedShipmentAlt}
                style={uiStyles.failedImage}
              />
              {failureContext && (
                <Text styles={{ root: uiStyles.failureContext }}>{failureContext}</Text>
              )}
              <Text styles={{ root: uiStyles.failureContext }}>{shipmentsStrings.failedHint}</Text>
            </Box>
          </Box>
        ) : (
          <Box className="shipments-body-container" sx={uiStyles.bodyContainer}>
            {rows.map((row) => (
              <ShipmentRow
                key={row.key}
                origin={row.origin}
                destination={row.destination}
                detailLeft={row.detailLeft}
                modeLabel={row.modeLabel}
                weightLbs={row.weightLbs}
                isQuoted={row.isQuoted}
                replySelectionEnabled={row.replySelectionEnabled}
                priceText={row.priceText}
                loadStatus={row.loadStatus}
                statusHistory={row.statusHistory}
                classification={row.classification}
                bolClassification={row.bolClassification}
                statuses={statuses}
                interactionLeadLabel={row.interactionLeadLabel}
                verifiedSender={row.verifiedSender}
                selected={selectionState.selectedIds.has(row.shipmentId)}
                isLoading={loadingShipments.has(row.shipmentId)}
                tmsLoadId={row.tmsLoadId}
                onOpen={() => handleShipmentClick(row)}
                onToggleSelect={(checked) => {
                  toggleSelection(row, checked);
                  shipmentActions.select({
                    mode: row.mode,
                    shipmentId: row.shipmentId,
                    selected: checked,
                  });
                }}
                onBook={() => handleBookClick(row)}
              />
            ))}
          </Box>
        )}
      </Box>

      <OfferSelectionModal
        open={offerModal.open}
        offers={offerModal.offers}
        onCancel={handleOfferModalCancel}
        onSubmit={handleOfferModalSubmit}
      />
    </Box>
  );
}
